import { IImage } from "@faire/web-api--source/indigofair/data/IImage";
import { IMessengerMessage } from "@faire/web-api--source/indigofair/data/IMessengerMessage";
import clone from "lodash/clone";
import compact from "lodash/compact";

import { ImageFile } from "../types";
import { uploadImageFile } from "../uploadImageFile";

export interface IImageOrFile {
  file?: ImageFile;
  image?: IImage;
}

export interface MessageModel {
  authorToken: string;
  data: Partial<IMessengerMessage>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error?: any;
  images?: readonly File[];
  productTokens: string[];
}

export const createNewMessage = (
  authorToken: string,
  message: Partial<IMessengerMessage>,
  images: readonly File[] | undefined,
  products: string[] = []
): MessageModel => ({
  authorToken,
  data: {
    created_at: Date.now(),
    ...message,
    author_token: authorToken,
  },
  images,
  productTokens: products,
});

export const updateMessage = (
  message: MessageModel,
  messageData: Partial<IMessengerMessage>,
  images: readonly File[] | undefined,
  products: string[] = []
): MessageModel => ({
  ...message,
  data: messageData,
  images,
  error: undefined,
  productTokens: clone(products),
});

/*
 * Getters
 */

export const getToken = (message: MessageModel): string => {
  return message.data.token!;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getError = (message: MessageModel): any => {
  return message.error;
};

export const getCreatedAt = (message: MessageModel): number | undefined => {
  return message.data.created_at;
};

export const getDeletableUntil = (
  message: MessageModel
): number | undefined => {
  return message.data.deletable_until;
};

export const shouldPopUp = (message: MessageModel): boolean => {
  if (message.data.should_pop_up && message.data.created_at) {
    const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
    if (sevenDaysAgo < message.data.created_at) {
      return true;
    }
  }
  return false;
};

export const getText = (message: MessageModel): string | undefined => {
  return message.data.text;
};

export const getImages = (message: MessageModel): IImageOrFile[] => {
  if (message.images) {
    return message.images.map((image) => ({
      file: Object.assign(image, { preview: URL.createObjectURL(image) }),
    }));
  }

  if (message.data.images) {
    return message.data.images.map((image) => ({
      image,
    }));
  }

  return [];
};

export const isDeleted = (message: MessageModel): boolean => {
  return !!message.data.deleted;
};

export const getAuthorFirstName = (message: MessageModel): string => {
  return message.data.author_first_name ?? "";
};

export const setError = (
  message: MessageModel,
  errorMessage: string
): MessageModel => ({
  ...message,
  error: errorMessage,
});

export const uploadImages = async (
  message: MessageModel
): Promise<string[] | undefined> => {
  const images =
    message.images && (await Promise.all(message.images.map(uploadImageFile)));
  return images && compact(images.map((image) => image.token));
};
